/**
 * 審核單的顯示欄位共用轉換（票 33）。
 *
 * `initiator` 是一個**顯示字串**，而且格式不只一種：
 *   - 執行期由 `ApprovalFlow` 組出來的是「姓名(員編)」（半形括號）
 *   - 種子與舊資料是「姓名（部門）」（全形括號）
 *
 * 所以任何「這張單是不是我發動的」都不能拿整串去比 —— 看板原本就是這樣寫的
 * （`a.initiator === \`${name}(${employeeId})\``），永遠比不中，「我發動的」恆為 0。
 * 審核中心早就有剝括注的寫法，這裡把它抽出來給兩邊共用。
 *
 * ⚠️ 這是**權宜**：真正該比的是發動者的 id。等審核單帶得出 `initiatorCustomerId`
 * （後端接手審核流程時），改成比 id 並把這支收掉。
 */

/** 「姓名（部門）」「姓名(員編)」→ 純姓名 */
export const initiatorName = (approval) =>
  String((approval && approval.initiator) || "").replace(/[（(].*$/, "").trim();

/** 這張單是不是這個人發動的（比姓名，不比顯示字串） */
export const isInitiatedBy = (approval, user) => {
  const name = user && user.name ? String(user.name).trim() : "";
  return !!name && initiatorName(approval) === name;
};

/* ————— 誰能審核（票 38） ————— */

/**
 * 一個角色能不能當審核者。
 *
 * ## 為什麼不是比角色代碼
 *
 * 原本的規則是「代碼是 `Gatekeeper` 或 `Overseer`」。但**角色是使用者自己新增的**
 * （ADR-0002 補述三）—— 客戶建了自己命名的角色（例如「維運主管」）就永遠不會被
 * 認成審核者；把 `Gatekeeper` 改名或停用，審核流程直接斷掉。反過來，一個叫
 * `Overseer` 但權限全 `Hidden` 的角色，仍然會被列為候選。
 *
 * 也就是「誰能審核」跟權限設定頁**完全脫鉤**，而那一頁明明就有
 * 「審核功能（三關簽核）」這一列可以設可編輯／唯讀／隱藏。
 *
 * 現在改成看能力：**`Approval` 模組是 `Edit`** 才算。客戶自建的角色只要把那一列
 * 設成可編輯就會出現在候選裡，不必改任何程式。
 *
 * ## 為什麼是 Edit 不是 View
 *
 * 「唯讀」在權限設定頁的定義是「僅檢視」—— 看得到審核單，但按不了核准／退回。
 * 一個按不了的人不該出現在「請指定簽核人」的清單裡。
 */
export function roleCanApprove(modules) {
  return (modules || []).some(
    (m) => m && m.module_key === "Approval" && m.access === "Edit"
  );
}
